import type { HandLegacyActionName } from './legacyActionNames';
import { AMPLITUDE_PRODUCT_EVENT_REGISTRY } from './productEventMeta';
import {
  AmplitudeProductEvent,
  type AmplitudeProductEventName,
} from './productEvents';

/**
 * Preferred `UserAction.action` / legacy envelope string per bucket-(b) product event.
 * Call sites use {@link AmplitudeProductEvent}; this map is for migration/dual-emit only.
 */
const CANONICAL_LEGACY_OVERRIDES: Partial<
  Record<AmplitudeProductEventName, HandLegacyActionName>
> = {
  [AmplitudeProductEvent.SEARCH_RESULT_CLICK]: 'search_result_click',
  [AmplitudeProductEvent.RESOURCE_VIEW]: 'ResourceView',
  [AmplitudeProductEvent.LOGIN_SUCCESS]: 'LoginSuccess',
  [AmplitudeProductEvent.CONTENT_ISSUE]: 'ContentIssue',
  [AmplitudeProductEvent.SEARCH_SUGGESTION_CLICKED]: 'searchSuggestionClicked',
};

const pickCanonicalLegacy = (
  legacyNames: readonly string[],
  productName: AmplitudeProductEventName,
): HandLegacyActionName => {
  const override = CANONICAL_LEGACY_OVERRIDES[productName];
  if (override) {
    return override;
  }
  if (legacyNames.includes(productName)) {
    return productName;
  }
  const snake = legacyNames.find(
    name => name.includes('_') && !name.includes('.'),
  );
  const fallback = legacyNames[0];
  if (!fallback) {
    throw new Error(
      `No legacy names for product event ${productName} in registry`,
    );
  }
  return snake ?? fallback;
};

const buildProductEventCanonicalLegacyAction = (): Record<
  AmplitudeProductEventName,
  HandLegacyActionName
> => {
  const map = {} as Record<AmplitudeProductEventName, HandLegacyActionName>;
  for (const meta of AMPLITUDE_PRODUCT_EVENT_REGISTRY) {
    map[meta.name] = pickCanonicalLegacy(meta.legacyNames, meta.name);
  }
  return map;
};

export const PRODUCT_EVENT_CANONICAL_LEGACY_ACTION =
  buildProductEventCanonicalLegacyAction();
